// Crafting screens (UI-4; ARCH §3.3 recipes). The 3×3 crafting-table window and
// the shared craft-grid machinery reused by the 2×2 inventory grid. A derived,
// take-only output slot is recomputed from the grid after every change; taking it
// consumes one of each ingredient (craft-all on shift). Client-only (ENG-1).
import { Inventory, emptyStack, isEmpty } from '../../../sim/game/inventory';
import { matchCrafting } from '../../../sim/game/crafting';
import { canCraftNow, fillCraftGrid, isCraftingRecipe, recipeFitsGrid } from '../../../sim/game/recipe_book';
import { h } from '../h';
import type { Registries } from '../../../sim/registry/load';
import { openWindow, refRange, returnToInventory, type SlotRef, type WindowConfig } from './container';
import type { Game } from '../../boot';

export function maxStackFn(reg: Registries): (item: number) => number {
  return (item) => reg.items.byName.get(reg.items.nameOf[item] ?? '')?.maxStack ?? 64;
}

/** The grid + output pair + recompute/consume, shared by inventory & table. */
export interface CraftGrid {
  craft: Inventory;
  output: Inventory;
  gridRefs: SlotRef[];
  outputRef: SlotRef;
  recompute(): void;
  consume(): void;
}

export function makeCraftGrid(game: Game, width: number): CraftGrid {
  const max = maxStackFn(game.registries);
  const craft = new Inventory(width * width, max);
  const output = new Inventory(1, max);
  const gridRefs = refRange(craft, 0, craft.size, undefined, 'craft', 'Crafting');
  const outputRef: SlotRef = { slots: output, index: 0, role: 'output', testId: 'output', title: 'Crafting Result' };
  let lastResult = emptyStack(); // the result the grid currently yields (M24 crafted stat)
  const recompute = (): void => {
    const m = matchCrafting(game.registries, craft.slots, width);
    lastResult = m ? m.result : emptyStack();
    output.set(0, lastResult);
  };
  const consume = (): void => {
    if (!isEmpty(lastResult)) { // one output stack was just taken → count it crafted
      const name = game.registries.items.nameOf[lastResult.item];
      if (name) game.onCraft?.(name, lastResult.count);
    }
    for (let i = 0; i < craft.size; i++) {
      const s = craft.get(i);
      if (s.count > 0) craft.set(i, s.count <= 1 ? emptyStack() : { ...s, count: s.count - 1 });
    }
    recompute();
  };
  return { craft, output, gridRefs, outputRef, recompute, consume };
}

// ── Player-inventory panel shared by every window ─────────────────────────────
// The layout and the shift-click router MUST share one set of SlotRef instances:
// openWindow maps quick-move targets to window slots by object identity, so a
// fresh ref (a new object) would not be found. Build these once per window.
export interface PlayerRefs {
  pinv: Inventory;
  storage: SlotRef[]; // indices 9..35
  hotbar: SlotRef[];  // indices 0..8
}

export function playerRefs(pinv: Inventory): PlayerRefs {
  return {
    pinv,
    storage: refRange(pinv, 9, 36, undefined, 'inv'),
    hotbar: refRange(pinv, 0, 9, undefined, 'inv'),
  };
}

/** Three storage rows then the hotbar row (with a gap before the hotbar). */
export function playerRows(pr: PlayerRefs): (SlotRef | null)[][] {
  const rows: (SlotRef | null)[][] = [];
  for (let r = 0; r < 3; r++) rows.push(pr.storage.slice(r * 9, r * 9 + 9));
  rows.push([null]); // gap
  rows.push(pr.hotbar);
  return rows;
}

/** Shift-click routing: grid/output → inventory; hotbar↔storage. Returns the
 *  shared ref instances so openWindow can resolve them by identity. */
export function playerQuickTargets(ref: SlotRef, pr: PlayerRefs, gridSlots: Set<object>): SlotRef[] {
  if (gridSlots.has(ref.slots) || ref.role === 'output') {
    return [...pr.storage, ...pr.hotbar];
  }
  if (ref.slots === pr.pinv) {
    if (ref.index >= 0 && ref.index < 9) return pr.storage;
    if (ref.index >= 9 && ref.index < 36) return pr.hotbar;
    return [...pr.storage, ...pr.hotbar]; // armor/offhand
  }
  return [];
}

/** Recipe book (M24): a toggle reveals the recipes unlocked AND that actually fit
 *  this grid width; clicking one ghost-fills the grid from the player inventory
 *  (fillCraftGrid). A "Show: Craftable" toggle filters out recipes the player
 *  doesn't currently have ingredients for; when off, unavailable ones stay
 *  listed but dimmed (canCraftNow). Shared by the 2×2 inventory grid and the
 *  3×3 table. */
export function makeRecipeBookFooter(
  game: Game, g: CraftGrid, width: number,
): { footer: HTMLElement; onOpen(repaint: () => void): void; refresh(): void } {
  let repaint = (): void => {};
  let bookOpen = false;
  let craftableOnly = false;
  let query = '';
  const bookGrid = h('div', { class: 'bb-recipebook' });
  const rebuildBook = (): void => {
    clearBook(bookGrid);
    const player = game.playerState.inventory;
    const q = query.trim().toLowerCase();
    const candidates = game.registries.recipes.list.filter((r) =>
      isCraftingRecipe(r) && recipeFitsGrid(r, width) && game.recipeBook.isUnlocked(r.name) &&
      (!q || prettyName(r.result.item).toLowerCase().includes(q)));
    for (const r of candidates) {
      const craftable = canCraftNow(r, player, game.registries, width);
      if (craftableOnly && !craftable) continue;
      const id = game.registries.items.idOf.get(r.result.item);
      if (id === undefined) continue;
      const cell = h('button', {
        class: `bb-recipe${craftable ? '' : ' bb-recipe-unavailable'}`,
        'data-recipe': r.name,
        title: prettyName(r.result.item),
      },
        h('div', { class: 'bb-slot-icon', style: { backgroundImage: `url(${game.icons.get(id)})` } }),
        r.result.count && r.result.count > 1 ? h('span', { class: 'bb-slot-count' }, String(r.result.count)) : null);
      cell.onclick = () => { fillCraftGrid(r, g.craft, player, game.registries, width); g.recompute(); repaint(); };
      bookGrid.append(cell);
    }
  };
  const toggle = h('button', { class: 'bb-btn bb-recipebook-toggle', 'data-testid': 'recipe-book-toggle' }, 'Recipe Book');
  const filterBtn = h('button', { class: 'bb-btn bb-recipebook-filter', 'data-testid': 'recipe-book-filter' });
  const search = h('input', {
    class: 'bb-input bb-recipebook-search', type: 'text', placeholder: 'Search…', 'data-testid': 'recipe-book-search',
  });
  search.oninput = () => { query = search.value; rebuildBook(); };
  const syncFilterLabel = (): void => {
    filterBtn.textContent = craftableOnly ? 'Show: Craftable' : 'Show: All';
    filterBtn.classList.toggle('bb-filter-on', craftableOnly);
  };
  syncFilterLabel();
  const setBookVisible = (open: boolean): void => {
    bookOpen = open;
    bookGrid.style.display = open ? 'grid' : 'none';
    filterBtn.style.display = open ? '' : 'none';
    search.style.display = open ? '' : 'none';
    if (open) rebuildBook();
  };
  filterBtn.onclick = () => { craftableOnly = !craftableOnly; syncFilterLabel(); rebuildBook(); };
  toggle.onclick = () => setBookVisible(!bookOpen);
  setBookVisible(false);
  const controls = h('div', { class: 'bb-recipebook-controls' }, toggle, search, filterBtn);
  const footer = h('div', { class: 'bb-recipebook-panel' }, controls, bookGrid);
  return {
    footer,
    onOpen: (r) => { repaint = r; },
    refresh: () => { if (bookOpen) rebuildBook(); },
  };
}

function clearBook(el: HTMLElement): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Title-cases an item id for display (same convention as stats.ts/trade.ts). */
function prettyName(id: string): string {
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function openCraftingTable(game: Game): void {
  const pr = playerRefs(game.playerState.inventory);
  const g = makeCraftGrid(game, 3);
  const gridSet = new Set<object>([g.craft, g.output]);

  const layout: (SlotRef | null)[][] = [
    g.gridRefs.slice(0, 3),
    [...g.gridRefs.slice(3, 6), null, g.outputRef],
    g.gridRefs.slice(6, 9),
    [null],
    ...playerRows(pr),
  ];

  const { footer, onOpen, refresh } = makeRecipeBookFooter(game, g, 3);

  const cfg: WindowConfig = {
    title: 'Crafting',
    layout,
    rowLabels: { 0: 'Crafting', 4: 'Inventory' },
    quickTargets: (ref) => playerQuickTargets(ref, pr, gridSet),
    onChange: () => { g.recompute(); refresh(); },
    onOutputTaken: g.consume,
    onClose: (cursor) => returnToInventory(game, [cursor, ...g.craft.slots]),
    onOpen,
    footer,
  };
  openWindow(game, cfg);
}
